import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { UserDetailsContext } from '@/modules/RegisterLayout/contexts/UserDetailsContextProvider';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import { razorPayKey } from '@/utils/env.constants';
import { dataLayer } from '@/utils/dataLayer';
import { Checkbox, Typography } from '@pw-tech/omni-ui';
import { useLeadCapture } from '@/modules/RegisterLayout/hooks/useLeadCapture';
import { useInitiatePayment } from '@/modules/RegisterLayout/hooks/useInitiatePayment';
import { useVerifyPayment } from '@/modules/RegisterLayout/hooks/useVerifyPayment';
import Image from 'next/image';
import PaymentStatusModal from '../../ApplicationFee/RegistrationFee/PaymentStatusModal/PaymentStatusModal';
import useIsMobile from '@/hooks/useIsMobile';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import FooterPortal from '@/modules/common/Portals/FooterPortal/FooterPortal';
import { getNextStep } from '@/modules/common/utils';
import { InfoIcon } from 'lucide-react';
import { useGetAdmissionFee } from '@/modules/RegisterLayout/hooks/useGetAdmissionFee';
declare global {
  interface Window {
    Razorpay: any;
  }
}
const PaymentPending = () => {
  //   States
  const [isDeclarationChecked, setIsDeclarationChecked] = useState<boolean>(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState({
    success: false,
    failed: false
  });
  const [paymentProgress, setPaymentProgress] = useState(false);

  //  Contexts
  const {
    user
  } = useContext(UserDetailsContext);
  const {
    registrationDetails,
    submitRegisterForm
  } = useContext(RegistrationContext);
  const {
    selectedCourseData
  } = useContext(CourseContext);
  const router = useRouter();
  const {
    courseId,
    coming_from_page,
    section
  } = router.query;
  const {
    mutateAsync: asyncLeadCapture
  } = useLeadCapture();
  const {
    mutateAsync: asyncInitiatePayment,
    isPending: isInitiatingPayment
  } = useInitiatePayment();
  const {
    mutateAsync: asyncVerifyPayment,
    isPending: isVerifyingPayment
  } = useVerifyPayment();
  const {
    data: admissionFee
  } = useGetAdmissionFee({});
  const isMobileScreen = useIsMobile();
  const utmQuery = JSON.parse(sessionStorage.getItem('query') || '{}');
  useEffect(() => {
    const ele = document.createElement('script');
    ele.type = 'text/javascript';
    ele.async = true;
    ele.src = 'https://checkout.razorpay.com/v1/checkout.js';
    document.body.appendChild(ele);
    return () => {
      if (ele) {
        document.body.removeChild(ele);
        const razorpayContainer = document.querySelectorAll('.razorpay-container');
        razorpayContainer.forEach(container => {
          container.remove();
        });
      }
    };
  }, []);
  const handlePayment = async () => {
    // Capture Lead
    asyncLeadCapture();

    // Initiate Payment
    asyncInitiatePayment({
      coupon: ''
    }, {
      onSuccess: (paymentData: any) => {
        const orderResponse = paymentData?.data;
        const data = {
          key: razorPayKey,
          amount: admissionFee?.feeStructureDetails,
          currency: 'INR',
          order_id: orderResponse?.orderId,
          name: `Physics Wallah Pvt. Ltd.`,
          prefill: {
            name: `${user?.firstName} ${user?.lastName}`,
            email: registrationDetails?.email,
            contact: `${user?.isdCode}${user?.phoneNumber}`
          },
          handler: async (response: any) => {
            try {
              asyncVerifyPayment({
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                signature: response.razorpay_signature
              }, {
                onSuccess: (paymentResponse: any) => {
                  dataLayer({
                    user_id: user?._id,
                    user_name: `${user?.firstName} ${user?.lastName}`,
                    email: registrationDetails?.email,
                    mobile_number: `${user?.isdCode}${user?.phoneNumber}`,
                    // school_name: activeBatch?.school.name,
                    // course_name: activeBatch?.name,
                    payment_status: 'success',
                    order_id: response.razorpay_order_id,
                    amount: orderResponse?.data?.batchDetails.price,
                    currency: 'INR',
                    event: 'payment_status',
                    ...utmQuery
                  });
                  asyncLeadCapture();
                  setPaymentModalOpen({
                    ...paymentModalOpen,
                    success: true
                  });
                  setPaymentProgress(false);
                },
                onError: (error: any) => {
                  dataLayer({
                    user_id: user?._id,
                    user_name: `${user?.firstName} ${user?.lastName}`,
                    email: registrationDetails?.email,
                    mobile_number: `${user?.isdCode}${user?.phoneNumber}`,
                    // school_name: activeBatch?.school.name,
                    // course_name: activeBatch?.name,
                    payment_status: 'fail',
                    order_id: response.razorpay_order_id,
                    amount: orderResponse?.data?.batchDetails.price,
                    currency: 'INR',
                    event: 'payment_status',
                    ...utmQuery
                  });
                  asyncLeadCapture();
                  setPaymentProgress(false);
                }
              });
            } catch (error: any) {
              dataLayer({
                event: 'ioi_application_payment_status',
                ioi_proceed_to_pay: 'yes',
                payment_status: 'failed',
                coming_from_page: coming_from_page ?? '',
                section: section ?? '',
                course_selected: selectedCourseData?.name ?? '',
                application_type: 'cee',
                ...utmQuery
              });
              dataLayer({
                user_id: user?._id,
                user_name: `${user?.firstName} ${user?.lastName}`,
                email: registrationDetails?.email,
                mobile_number: `${user?.isdCode}${user?.phoneNumber}`,
                // school_name: activeBatch?.school?.name,
                // course_name: activeBatch?.name,
                payment_status: 'fail',
                order_id: response.razorpay_order_id,
                amount: orderResponse?.courseDetails?.price,
                currency: 'INR',
                event: 'payment_status',
                ...utmQuery
              });
              setPaymentProgress(false);
            }
          }
        };
        const razorpay = new window.Razorpay(data);
        razorpay?.open();
        razorpay?.on('payment.failed', (response: any) => {
          setPaymentModalOpen({
            ...paymentModalOpen,
            failed: true
          });
          asyncLeadCapture();
          dataLayer({
            user_id: user?._id,
            user_name: `${user?.firstName} ${user?.lastName}`,
            email: registrationDetails?.email || user?.email,
            mobile_number: `${user?.isdCode}${user?.phoneNumber}`,
            //   school_name: activeBatch?.school.name,
            //   course_name: activeBatch?.name,
            payment_status: 'fail',
            order_id: response.error.metadata.order_id,
            amount: orderResponse?.data?.batchDetails.price,
            currency: 'INR',
            event: 'payment_status',
            ...utmQuery
          });
        });
        setPaymentProgress(false);
      },
      onError: (error: any) => {
        setPaymentProgress(false);
      }
    });
  };
  const handleContinue = async () => {
    if (paymentModalOpen?.failed) {
      setPaymentModalOpen({
        success: false,
        failed: false
      });
      return;
    }
    await submitRegisterForm({
      step: getNextStep(router.query.step, registrationDetails?.step)
    }, true);
  };
  return <article data-sentry-component="PaymentPending" data-sentry-source-file="index.tsx">
      <div className="flex flex-col gap-5">
        <Typography variant="regular" className="bg-[#FFF6E5] md:!items-center flex gap-2 p-4 rounded-lg" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
          {isMobileScreen ? <div className="flex gap-2 ">
              <Image src="/images/registerPage/trophy.png" alt="trophy-icon" className="!h-8 !w-8 rounded-full" width={28} height={28} />
              <div className="flex">
                <p className="font-semibold">
                  Heads up, future innovator
                  <span className="font-normal">
                    {' '}
                    you’re selected for {registrationDetails?.campusAlloted || ''} 2026{' '}
                    {selectedCourseData?.name || ''}
                  </span>
                </p>
              </div>
            </div> : <>
              <div className="">
                <Image src="/images/registerPage/trophy.png" alt="trophy-icon" className="items-start rounded-full" width={28} height={28} />
              </div>
              <div className="flex">
                <p className="font-semibold">
                  Heads up, future innovator -
                  <span className="font-normal">
                    {' '}
                    you’re selected for {registrationDetails?.campusAlloted || ''} 2026 for{' '}
                    {selectedCourseData?.name || ''}
                  </span>
                </p>
              </div>
            </>}
        </Typography>
        <Typography variant="heading3" weight="semi-bold" className="!text-2xl" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
          Pay Admission Fee
        </Typography>
        <div className="shadow-[0px_8px_24px_0px_rgba(0,0,0,0.06)] p-4">
          <Typography variant="regular" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            Please pay admission fee to confirm your seat at{' '}
            {registrationDetails?.campusAlloted || ''}
          </Typography>
          <div className="flex items-center gap-1">
            <Typography variant="regular" data-sentry-element="Typography" data-sentry-source-file="index.tsx">Total Fee</Typography>
            <Typography variant="heading2" weight="semi-bold" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
              ₹{admissionFee?.feeStructureDetails}
            </Typography>
          </div>
          <div className="flex items-start gap-1">
            <Checkbox value="declaration" checked={isDeclarationChecked} onClick={e => setIsDeclarationChecked(e.currentTarget.checked)} data-sentry-element="Checkbox" data-sentry-source-file="index.tsx" />
            <Typography variant="regular" className="!text-sm !text-[#757575] mt-[2px]" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
              I hereby declare that information provided by me is true to the best of my knowledge
              and I agree to the terms and conditions.
            </Typography>
          </div>
        </div>
        <div className="flex gap-1 my-1">
          <div className="flex flex-shrink-0 ">
            <InfoIcon size={20} data-sentry-element="InfoIcon" data-sentry-source-file="index.tsx" />
          </div>
          <Typography variant="regular" className="!text-sm !text-[#757575]" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            Admission Fee are non-refundable in any circumstances.
          </Typography>
        </div>
      </div>

      <div className="h-8" />
      <div className="py-2">
        <FooterPortal label={admissionFee?.feeStructureDetails ? `Continue to Pay ₹${admissionFee?.feeStructureDetails}` : 'Continue'} type="button" btnWrapperClass="relative sm:left-[30%]" onClick={handlePayment} disabled={!isDeclarationChecked || registrationDetails?.isAdmissionFeePaid || paymentProgress || !admissionFee?.feeStructureDetails} isFormSubmitting={isInitiatingPayment || isVerifyingPayment || paymentProgress} data-sentry-element="FooterPortal" data-sentry-source-file="index.tsx" />
      </div>

      {/* Payment Status Modal */}
      <PaymentStatusModal isOpen={paymentModalOpen?.success || paymentModalOpen?.failed} type={paymentModalOpen?.success ? 'success' : paymentModalOpen?.failed ? 'failed' : null} onClose={() => {
      setPaymentModalOpen({
        success: false,
        failed: false
      });
    }} onContinue={handleContinue} data-sentry-element="PaymentStatusModal" data-sentry-source-file="index.tsx" />
    </article>;
};
export default PaymentPending;